import { BUSINESS, addressSingleLine } from "../config/business";
import { relabel } from "./displayNames";

const LOGO = "https://www.jc3wealthlegacy.com/jc3-logo-horizontal-on-dark.svg";
const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const money = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—");

const totalRow = (label, cents, strong) =>
  `<tr class="${strong ? "strong" : ""}"><td>${label}</td><td class="num">${money(cents)}</td></tr>`;

const invoiceHtml = (inv) => {
  const t = inv.totals || {};
  const paid = (t.total_cents || 0) - (t.balance_cents || 0);
  const lines = (inv.lines || []).map((l) => `
    <tr>
      <td>${esc(relabel(l.description))}${l.is_pass_through ? ' <span class="muted">(at cost)</span>' : ""}</td>
      <td class="num">${esc(l.quantity ?? 1)}</td>
      <td class="num">${money(l.amount_cents)}</td>
    </tr>`).join("");
  const payments = (inv.payments || []).map((p) => `
    <tr><td>${date(p.at || p.created_at)}</td><td>${esc(p.method || "Payment")}</td><td class="num">${money(p.amount_cents)}</td></tr>`).join("");

  return `<!doctype html>
<html><head><title>${esc(inv.number)} · ${esc(BUSINESS.shortName)}</title>
<link href="https://fonts.googleapis.com/css2?family=Libre+Caslon+Display&family=Hanken+Grotesk:wght@400;500&display=swap" rel="stylesheet">
<style>
  @page { size: Letter; margin: 14mm; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
  body { margin: 0; font-family: 'Hanken Grotesk', sans-serif; color: #2A1C15; background: #DCCFB9; font-size: 13px; }
  .head { background: #14281E; padding: 28px 36px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(200,164,106,.45); }
  .head img { height: 34px; }
  .head .meta { color: #BDB6A6; font-size: 11px; text-align: right; line-height: 1.6; }
  .wrap { padding: 36px; }
  h1 { font-family: 'Libre Caslon Display', serif; font-weight: 400; text-transform: uppercase; letter-spacing: .04em; font-size: 40px; margin: 0 0 6px; }
  .num-line { color: #5E4D40; margin-bottom: 28px; }
  .grid { display: flex; gap: 24px; margin-bottom: 28px; }
  .box { flex: 1; background: #E7DDCB; padding: 18px 20px; }
  .label { color: #5E4D40; font-size: 11px; letter-spacing: .1em; text-transform: uppercase; margin-bottom: 6px; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; color: #5E4D40; font-weight: 500; font-size: 11px; letter-spacing: .1em; text-transform: uppercase; padding: 10px 0; border-bottom: 1px solid rgba(42,28,21,.18); }
  td { padding: 12px 0; border-bottom: 1px solid rgba(42,28,21,.18); vertical-align: top; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  th.num { text-align: right; }
  .muted { color: #5E4D40; }
  .panel { background: #E7DDCB; padding: 8px 20px 4px; margin-bottom: 24px; }
  .totals { width: 320px; margin-left: auto; }
  .totals td { padding: 8px 0; }
  .totals .strong td { font-weight: 500; font-size: 15px; border-bottom: 0; border-top: 1px solid #C8A46A; }
  .balance { font-family: 'Libre Caslon Display', serif; font-size: 30px; }
  .tag { display: inline-block; border: 1px solid currentColor; padding: 2px 8px; font-size: 11px; letter-spacing: .08em; text-transform: uppercase; }
  .foot { margin-top: 36px; padding-top: 14px; border-top: 1px solid rgba(42,28,21,.18); color: #5E4D40; font-size: 11px; line-height: 1.7; }
</style></head>
<body>
  <div class="head">
    <img src="${LOGO}" alt="${esc(BUSINESS.shortName)}" />
    <div class="meta">${esc(addressSingleLine())}<br/>${esc(BUSINESS.phone)} · ${esc(BUSINESS.email)}<br/>${esc(BUSINESS.website)}</div>
  </div>
  <div class="wrap">
    <h1>Invoice</h1>
    <div class="num-line">${esc(inv.number)} · Issued ${date(inv.created_at)}${inv.due_date ? ` · Due ${date(inv.due_date)}` : ""}</div>
    <div class="grid">
      <div class="box"><div class="label">Billed to</div><div>${esc(inv.client_name || "—")}</div></div>
      <div class="box"><div class="label">Status</div><span class="tag">${esc(t.status || "Draft")}</span></div>
      <div class="box"><div class="label">Balance due</div><div class="balance">${money(t.balance_cents)}</div></div>
    </div>
    <div class="panel">
      <table>
        <thead><tr><th>Service</th><th class="num">Qty</th><th class="num">Amount</th></tr></thead>
        <tbody>${lines || '<tr><td colspan="3" class="muted">No line items.</td></tr>'}</tbody>
      </table>
    </div>
    <table class="totals">
      ${totalRow("Services subtotal", t.services_subtotal_cents)}
      ${t.pass_through_cents ? totalRow("State fees at cost", t.pass_through_cents) : ""}
      ${t.processing_fee_cents ? totalRow("Processing fee", t.processing_fee_cents) : ""}
      ${t.tax_cents ? totalRow("Tax", t.tax_cents) : ""}
      ${t.discount_cents ? totalRow("Discount", -t.discount_cents) : ""}
      ${totalRow("Total", t.total_cents, true)}
      ${paid ? totalRow("Paid", -paid) : ""}
      ${totalRow("Balance due", t.balance_cents, true)}
    </table>
    ${payments ? `<div class="panel" style="margin-top:24px"><table><thead><tr><th>Date</th><th>Payment</th><th class="num">Amount</th></tr></thead><tbody>${payments}</tbody></table></div>` : ""}
    ${inv.notes ? `<div class="label" style="margin-top:12px">Notes</div><div>${esc(inv.notes)}</div>` : ""}
    <div class="foot">© 2026 ${esc(BUSINESS.legalName)} · ${esc(BUSINESS.tagline)}<br/>Thank you for your business. Questions? Call ${esc(BUSINESS.phone)} or email ${esc(BUSINESS.email)}.</div>
  </div>
  <script>window.onload = () => setTimeout(() => window.print(), 450);</script>
</body></html>`;
};

export const printInvoicePDF = (inv) => {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    alert("Pop-up blocked. Allow pop-ups to download the invoice PDF.");
    return;
  }
  w.document.open();
  w.document.write(invoiceHtml(inv));
  w.document.close();
};
